<template>
  <article class="sortable-selector">
    <!-- Search or Create Checks -->
    <div class="is-sticky-header columns is-vcentered is-gapless">
      <div :class="['column', checklist.custom_check_weighting ? 'is-8 mr-1' : '']">
      <server-data-selector
            :disabled="loading || checklist.locked || !canEditChecks"
            :data-tooltip="canEditChecks ? null : `You cannot add ${Naming.Checks.toLowerCase()} to a ${Naming.Checklist.toLowerCase()} with completed ${Naming.Inspections.toLowerCase()}`"
            searchable
            ref="checkSearch"
            :items="filteredChecklists"
            @input="pushCheck"
            :prompt-label="`Add an existing ${Naming.Check.toLowerCase()}`"
            classes="is-rounded has-tooltip-multiline"
            value-key="id"
            label-key="name"
            left-icon="search"
            :on-type="searchChecklists">
          <action-button
              slot="right"
              :data-tooltip="canEditChecks ? null : `You cannot add ${Naming.Checks.toLowerCase()} to a ${Naming.Checklist.toLowerCase()} with completed ${Naming.Inspections.toLowerCase()}`"
              :disabled="checklist.locked || !canEditChecks"
              @click="createQuickCheck"
              class="is-rounded is-cool-blue-darker has-tooltip-multiline"
              left-icon="plus">
            New {{ Naming.Check }}
          </action-button>
          <template #item="{ item }">
            <div class="flex-grow">
              <p class="has-text-weight-bold">{{ item.name }}</p>
              <p class="has-text-weight has-text-cool-blue-text">{{ item.task | truncate(100) }}</p>
            </div>
            <span class="tag is-rounded is-warning has-text-weight-bold" v-if="item.instant_fail">Instant Fail</span>
            <span class="tag is-rounded is-cool-blue ml" v-if="item.checklists_count">
              on {{ item.checklists_count }} {{ 'other checklist' | plural(item.checklists_count) }}
            </span>
          </template>
        </server-data-selector>
      </div>

      <div class="column is-4" v-if="checklist.custom_check_weighting">
        <div class="p-05 has-background-cool-blue is-flex is-align-items-center is-justify-content-space-between">
          <p class="mb-0 has-text-weight-semibold">
            {{ Naming.Checks }} Weighting Total/Percentage:
            <br>
            <span :class="{
                  'has-text-danger': totalsOk === false,
                  'has-text-success': totalsOk === true}"
                  class="has-text-weight-semibold">
                 <input
                     style="width: 70%; font-weight: 500"
                     class="input tag is-size-6-5 has-background-white"
                     type="number"
                     v-model="weightingTotal"
                     @input="setWeightingPercentage"
                     :min="0"
                     :step="5"
                 />
              <br>
              {{ weightingSum }} =
              {{ Number(weightingPercentage || 0).toFixed(2) }} %
            </span>
          </p>
          <action-button
              class="rounded-sml is-primary has-text-weight-semibold ml-05"
              left-icon="sync"
              @click="resetCheckWeightings">
              Reset Weightings
          </action-button>
        </div>
      </div>
    </div>

    <!-- No checkpoints -->
    <notification class="is-cool-blue has-text-centered p-2 is-marginless" v-if="!checklist.checks.length">
      <p class="has-text-weight-bold">No {{ Naming.Checks }} Added</p>
      <p>This {{ Naming.Checklist.toLowerCase() }} doesn’t have any {{Naming.Checks.toLowerCase()}} yet.</p>
      <p>Get started by selecting an existing {{Naming.Check.toLowerCase()}}, or creating a new {{Naming.Check.toLowerCase()}}.</p>
    </notification>

    <!-- Sort and remove checklists -->
    <draggable
      handle="#handle"
      :animation="200"
      :value="checklist.checks"
      @input="setChecks"
      group="checklists">
      <transition-group>
        <checkpoint
          v-for="(check, index) in checklist.checks"
          :key="check.id"
          :is-locked="checklist.locked"
          :custom-weighting="checklist.custom_check_weighting"
          :can-delete="canEditChecks"
          v-bind="{ check, index }"
          @onCheckUpdated="updateChecklistChecks"
          @weights="updateWeights"
          @onRemoveCheck="pullCheck"
          @deleteCheck="removeDeletedCheck"/>
      </transition-group>
    </draggable>
  </article>
</template>

<script>
import {mapState, mapMutations} from 'vuex'
import { newCheck } from '@/modals'
import Checkpoint from "@/views/checklist/partials/Checkpoint.vue";
import differenceBy from 'lodash/differenceBy'
import find from 'lodash/find'
import Search from '@/utils/search'

export default {

  components: {
    Checkpoint,
  },

  props: {
    canEditChecks: {
      type: Boolean,
      default: true
    }
  },

  data: () => ({
    loading: true,
    weightingTotal: 0,
    weightingPercentage: 0,
    weightingSum: 0
  }),

  created() {
    Search.searchByUri('', 'check/searchChecksList', this.$store, 0),
    this.loading = false
    if (this.checklist.checks.length) {
      this.weightingTotal = this.checksWeightingTotal
    }
    this.setChecksStartWeights()
  },

  methods: {
    ...mapMutations('checklist', [
      'setChecks',
      'addCheck',
      'removeCheck',
    ]),

    async createQuickCheck() {
      const check = await newCheck()
      if (check) {
        check.percentage_weight = 0
        this.addCheck(check)
      }
    },

    updateChecklistChecks(isChanged, updatedCheck) {
      if (isChanged) {
        this.$store.commit('checklist/updateChecks', updatedCheck)
      }
    },

    updateWeights(payload) {
      this.$store.commit('checklist/updateCheckWeight', payload)
      this.setWeightingPercentage()
    },

    resetCheckWeightings() {
      if (this.weightingTotal === 0) {
        this.weightingTotal = 100
      }
      this.$store.commit('checklist/resetCheckWeights',this.weightingTotal)
      this.setWeightingPercentage()
    },

    pushCheck(checkId) {
      this.$refs.checkSearch.search = ''
      this.$refs.checkSearch.selected = ''
      const check = find(this.checks, { id: checkId })
      check.percentage_weight = 0
      this.addCheck(check)
    },

    async pullCheck(index) {
      const { title, message, confirmText } = this.$lang.check.confirmRemove;
      const convertedTitle = await this.Convert(title)
      const convertedMessage = await this.Convert(message)
      const convertedConfirmText = await this.Convert(confirmText)
      if (await this.$confirm({
        title: convertedTitle,
        message: convertedMessage,
        confirmText: convertedConfirmText,
        confirmClass: 'is-danger'
      })) {
        this.removeCheck(index)
        this.setWeightingPercentage()
      }
    },

    removeDeletedCheck(checkId) {
      let index = this.checklist.checks.findIndex(check => check.id === checkId)
      if (index !== -1) {
        this.removeCheck(index)
        this.setWeightingPercentage()
      }
    },
    searchChecklists(text) {
      Search.debouncedSearchByUri(text, 'check/searchChecksList', this.$store)
    },
    setWeightingPercentage() {
      if (!Number(this.weightingTotal)) {
        this.weightingPercentage = 0
        return
      }
      let checksTotal = this.checklist.checks
          .map(check => Number(check.percentage_weight ?? 0))
          .reduce((sum, weight) => sum + weight, 0)
      this.weightingSum = checksTotal
      return this.weightingPercentage = (checksTotal / Number(this.weightingTotal)) * 100
    },
    setChecksStartWeights() {
      const checks = this.checklist.checks
      const hasWeightedChecks = checks.some(check => Number(check.percentage_weight) > 0)
      if (!hasWeightedChecks) {
        return this.resetCheckWeightings()
      }
      return this.setWeightingPercentage()
    }
  },

  computed: {
    ...mapState('checklist', [
      'checklist',
    ]),
    ...mapState('check', {
      checks: 'checkList',
    }),
    filteredChecklists() {
      return differenceBy(this.checks, this.checklist.checks, 'id')
    },
    checksWeightingTotal() {
      return this.$store.getters['checklist/checklistCustomTotal'];
    },
    totalsOk() {
      if (!this.checklist.custom_check_weighting) {
        return true
      }
      const emptyChecks = this.checklist.checks.filter(check => check.percentage_weight <= 0).length
      return this.weightingPercentage === 100 && emptyChecks < 1
    },
  },

  watch: {
    'checklist.custom_check_weighting'(newVal) {
      if (!newVal) {
        this.weightingTotal = 0
      } else {
        this.setWeightingPercentage()
      }
    },
    totalsOk(newValue) {
      this.$emit("totals-ok", newValue);
    }
  }

}
</script>
